import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, CircularProgress, Grid, Paper, Stack, Tab, Tabs, Tooltip,
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, AlertTitle, MenuItem, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Insights as InsightsIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import { PageHeader, StatCard } from '../../components/ui';

const ACTION_TYPE_LABELS = {
  price_update: 'Цена',
  ad_bid_update: 'Ставка рекламы',
  ad_campaign_pause: 'Пауза кампании',
  review_reply: 'Ответ на отзыв',
  supply_create: 'Поставка',
  recipe_save: 'Рецепт',
  lesson_save: 'Урок',
  other: 'Прочее',
};

const SIGNIFICANCE_COLORS = {
  high: 'success',
  medium: 'info',
  low: 'default',
  null: 'default',
};

const ACTION_TYPE_OPTIONS = [
  { value: '', label: 'Все типы' },
  ...Object.entries(ACTION_TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

function fmtNumber(v, digits = 2) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('ru-RU', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
}

function fmtPct(v, digits = 1) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(digits)}%`;
}

function effectivenessChip(value) {
  if (value == null) {
    return <Chip size="small" label="—" variant="outlined" />;
  }
  const v = Number(value);
  let color = 'default';
  if (v >= 0.7) color = 'success';
  else if (v < 0.3) color = 'error';
  else if (v >= 0.5) color = 'info';
  else color = 'warning';
  return <Chip size="small" color={color} label={v.toFixed(2)} variant="filled" />;
}

function significanceChip(sig) {
  if (!sig) return <Chip size="small" variant="outlined" label="—" />;
  return (
    <Chip
      size="small"
      variant={sig === 'low' ? 'outlined' : 'filled'}
      color={SIGNIFICANCE_COLORS[sig] || 'default'}
      label={sig}
    />
  );
}

export default function AiEffectiveness() {
  const { error: showError } = useNotify();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accessError, setAccessError] = useState(null);

  const [summary, setSummary] = useState(null);
  const [byActionType, setByActionType] = useState([]);
  const [byRecipe, setByRecipe] = useState({ top: [], bottom: [], degraded: [], total: 0 });
  const [timeseries, setTimeseries] = useState([]);
  const [actions, setActions] = useState([]);
  const [actionsTotal, setActionsTotal] = useState(0);

  const [tsActionType, setTsActionType] = useState('');
  const [tsDays, setTsDays] = useState(30);

  const [actionsFilters, setActionsFilters] = useState({
    action_type: '', marketplace: '', status: '',
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const handleAuthError = useCallback((e) => {
    const code = e?.response?.status;
    if (code === 401 || code === 403) {
      setAccessError({
        code,
        message: code === 401
          ? 'Необходима повторная авторизация.'
          : 'Нет прав на просмотр AI-эффективности. Запросите permission aiEffectiveness у администратора.',
      });
      return true;
    }
    return false;
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get('/ai/experiments/effectiveness/summary');
      setSummary(data);
    } catch (e) { if (!handleAuthError(e)) showError(e?.response?.data?.message || e.message); }
  }, [handleAuthError, showError]);

  const loadByActionType = useCallback(async () => {
    try {
      const { data } = await axios.get('/ai/experiments/effectiveness/by-action-type');
      setByActionType(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) { if (!handleAuthError(e)) showError(e?.response?.data?.message || e.message); }
  }, [handleAuthError, showError]);

  const loadByRecipe = useCallback(async () => {
    try {
      const { data } = await axios.get('/ai/experiments/effectiveness/by-recipe');
      setByRecipe({
        total: data?.total || 0,
        top: Array.isArray(data?.top) ? data.top : [],
        bottom: Array.isArray(data?.bottom) ? data.bottom : [],
        degraded: Array.isArray(data?.degraded) ? data.degraded : [],
      });
    } catch (e) { if (!handleAuthError(e)) showError(e?.response?.data?.message || e.message); }
  }, [handleAuthError, showError]);

  const loadTimeseries = useCallback(async () => {
    try {
      const params = { days: tsDays };
      if (tsActionType) params.action_type = tsActionType;
      const { data } = await axios.get('/ai/experiments/effectiveness/timeseries', { params });
      setTimeseries(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) { if (!handleAuthError(e)) showError(e?.response?.data?.message || e.message); }
  }, [tsActionType, tsDays, handleAuthError, showError]);

  const loadActions = useCallback(async () => {
    try {
      const params = { limit: 100 };
      Object.entries(actionsFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await axios.get('/ai/experiments/effectiveness/actions', { params });
      setActions(Array.isArray(data?.items) ? data.items : []);
      setActionsTotal(Number(data?.total) || 0);
    } catch (e) { if (!handleAuthError(e)) showError(e?.response?.data?.message || e.message); }
  }, [actionsFilters, handleAuthError, showError]);

  useEffect(() => {
    if (accessError) return;
    setLoading(true);
    Promise.all([loadSummary(), loadByActionType(), loadByRecipe(), loadTimeseries(), loadActions()])
      .finally(() => setLoading(false));
  }, [accessError, loadSummary, loadByActionType, loadByRecipe, loadTimeseries, loadActions]);

  useEffect(() => { if (!accessError) loadTimeseries(); }, [tsActionType, tsDays, accessError, loadTimeseries]);
  useEffect(() => { if (!accessError) loadActions(); }, [actionsFilters, accessError, loadActions]);

  const tsByDay = useMemo(() => {
    const map = new Map();
    const types = new Set();
    for (const r of timeseries) {
      types.add(r.action_type);
      if (!map.has(r.day)) map.set(r.day, { day: r.day });
      map.get(r.day)[r.action_type] = r.avg_effectiveness;
    }
    return {
      data: Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day)),
      types: Array.from(types),
    };
  }, [timeseries]);

  if (accessError) {
    return (
      <Box>
        <PageHeader title="AI: эффективность" subtitle="Stage 7 — experiment tracking" />
        <Alert severity={accessError.code === 401 ? 'warning' : 'error'}>
          <AlertTitle>{accessError.code === 401 ? 'Не авторизованы' : 'Нет доступа'}</AlertTitle>
          {accessError.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="AI: эффективность"
        subtitle="Замер реального эффекта рекомендаций и рецептов AI-агента (Stage 7)"
      />

      {loading && !summary && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      )}

      {/* StatCards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Всего действий"
              value={fmtNumber(summary.total_actions, 0)}
              subtitle={`pending: ${summary.pending}, executed: ${summary.executed_pending}`}
              icon={<ScienceIcon />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Измерено"
              value={fmtNumber(summary.measured_actions, 0)}
              subtitle={`scored: ${summary.scored_count}`}
              icon={<CheckIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Среднее effectiveness"
              value={summary.avg_effectiveness != null ? Number(summary.avg_effectiveness).toFixed(3) : '—'}
              subtitle={`+${summary.positive_count} / −${summary.negative_count}`}
              icon={<TrendingUpIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Деградировавшие рецепты"
              value={fmtNumber(summary.degraded_recipes_count, 0)}
              subtitle={`всего рецептов: ${summary.recipes_total}`}
              icon={<WarningIcon />}
              color="warning"
            />
          </Grid>
        </Grid>
      )}

      {/* Timeseries chart */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <InsightsIcon color="primary" />
          <Typography variant="h6" sx={{ flex: 1 }}>
            Динамика effectiveness по дням
          </Typography>
          <TextField
            size="small" select label="Тип действия" value={tsActionType}
            onChange={(e) => setTsActionType(e.target.value)} sx={{ minWidth: 180 }}
          >
            {ACTION_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" select label="Период" value={tsDays}
            onChange={(e) => setTsDays(Number(e.target.value))} sx={{ minWidth: 120 }}
          >
            {[7, 14, 30, 60, 90, 180].map((d) => (
              <MenuItem key={d} value={d}>{d} дней</MenuItem>
            ))}
          </TextField>
        </Stack>
        <Box sx={{ height: 280 }}>
          {tsByDay.data.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary">Нет данных за выбранный период</Typography>
            </Box>
          ) : (
            <ResponsiveContainer>
              <LineChart data={tsByDay.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis domain={[0, 1]} />
                <RTooltip />
                <Legend />
                {tsByDay.types.map((t, i) => (
                  <Line
                    key={t}
                    type="monotone"
                    dataKey={t}
                    name={ACTION_TYPE_LABELS[t] || t}
                    stroke={`hsl(${(i * 67) % 360},70%,45%)`}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`По типам действий (${byActionType.length})`} />
          <Tab label={`Топ рецепты (${byRecipe.top.length})`} />
          <Tab label={`Боттом / деградированные (${byRecipe.bottom.length} / ${byRecipe.degraded.length})`} />
          <Tab label={`Действия (${actionsTotal})`} />
        </Tabs>

        {/* Tab 0: by action_type */}
        {tab === 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Тип</TableCell>
                  <TableCell align="right">Всего</TableCell>
                  <TableCell align="right">Измерено</TableCell>
                  <TableCell align="right">Среднее eff</TableCell>
                  <TableCell align="right">Среднее delta</TableCell>
                  <TableCell align="right">Significant</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byActionType.map((r) => (
                  <TableRow key={r.action_type} hover>
                    <TableCell>{ACTION_TYPE_LABELS[r.action_type] || r.action_type}</TableCell>
                    <TableCell align="right">{fmtNumber(r.count, 0)}</TableCell>
                    <TableCell align="right">{fmtNumber(r.measured_count, 0)}</TableCell>
                    <TableCell align="right">
                      {r.avg_effectiveness != null ? effectivenessChip(r.avg_effectiveness) : '—'}
                    </TableCell>
                    <TableCell align="right">{fmtPct(r.avg_delta_pct)}</TableCell>
                    <TableCell align="right">
                      {fmtNumber(r.significant_count, 0)}
                      {r.significant_pct != null && (
                        <Typography component="span" color="text.secondary" variant="caption" sx={{ ml: 0.5 }}>
                          ({r.significant_pct}%)
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {byActionType.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>Данных пока нет</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tab 1: top recipes */}
        {tab === 1 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID рецепта</TableCell>
                  <TableCell>Заголовок</TableCell>
                  <TableCell>МП</TableCell>
                  <TableCell align="right">Effectiveness</TableCell>
                  <TableCell align="right">Применено</TableCell>
                  <TableCell>Создан</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byRecipe.top.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell><code>{r.id}</code></TableCell>
                    <TableCell><Tooltip title={r.tags.join(', ') || ''}><span>{r.title}</span></Tooltip></TableCell>
                    <TableCell>{(r.marketplace || 'all').toUpperCase()}</TableCell>
                    <TableCell align="right">{effectivenessChip(r.effectiveness)}</TableCell>
                    <TableCell align="right">{fmtNumber(r.applied_count, 0)}</TableCell>
                    <TableCell>{r.created || '—'}</TableCell>
                  </TableRow>
                ))}
                {byRecipe.top.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>Рецептов с applied_count ≥ 3 пока нет</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tab 2: bottom + degraded */}
        {tab === 2 && (
          <Box>
            {byRecipe.degraded.length > 0 && (
              <Alert severity="warning" sx={{ m: 2 }}>
                <AlertTitle>Деградировавшие рецепты ({byRecipe.degraded.length})</AlertTitle>
                effectiveness &lt; 0.3 при applied_count ≥ 10. Stage 7 cron уже автоматически создал lesson-learned для этих рецептов.
              </Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID рецепта</TableCell>
                    <TableCell>Заголовок</TableCell>
                    <TableCell>МП</TableCell>
                    <TableCell align="right">Effectiveness</TableCell>
                    <TableCell align="right">Применено</TableCell>
                    <TableCell>Статус</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byRecipe.bottom.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell><code>{r.id}</code></TableCell>
                      <TableCell>{r.title}</TableCell>
                      <TableCell>{(r.marketplace || 'all').toUpperCase()}</TableCell>
                      <TableCell align="right">{effectivenessChip(r.effectiveness)}</TableCell>
                      <TableCell align="right">{fmtNumber(r.applied_count, 0)}</TableCell>
                      <TableCell>
                        {r.degraded ? (
                          <Chip size="small" color="error" variant="filled" label="degraded" />
                        ) : (
                          <Chip size="small" variant="outlined" label="ok" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {byRecipe.bottom.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>Нет данных</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tab 3: actions list */}
        {tab === 3 && (
          <Box>
            <Stack direction="row" spacing={2} sx={{ p: 2 }}>
              <TextField
                size="small" select label="Тип" value={actionsFilters.action_type}
                onChange={(e) => setActionsFilters((f) => ({ ...f, action_type: e.target.value }))}
                sx={{ minWidth: 160 }}
              >
                {ACTION_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small" select label="МП" value={actionsFilters.marketplace}
                onChange={(e) => setActionsFilters((f) => ({ ...f, marketplace: e.target.value }))}
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="">Все</MenuItem>
                <MenuItem value="wb">WB</MenuItem>
                <MenuItem value="ozon">Ozon</MenuItem>
                <MenuItem value="yandex">Yandex</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
              </TextField>
              <TextField
                size="small" select label="Статус" value={actionsFilters.status}
                onChange={(e) => setActionsFilters((f) => ({ ...f, status: e.target.value }))}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">measured + executed</MenuItem>
                <MenuItem value="measured">measured</MenuItem>
                <MenuItem value="executed">executed</MenuItem>
              </TextField>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>МП</TableCell>
                    <TableCell>Выполнено</TableCell>
                    <TableCell align="right">Effectiveness</TableCell>
                    <TableCell align="right">Δ %</TableCell>
                    <TableCell>Significance</TableCell>
                    <TableCell>Primary</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {actions.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{ACTION_TYPE_LABELS[r.action_type] || r.action_type}</TableCell>
                      <TableCell>{(r.marketplace || '').toUpperCase()}</TableCell>
                      <TableCell>{r.executed_at ? new Date(r.executed_at).toLocaleString('ru-RU') : '—'}</TableCell>
                      <TableCell align="right">{effectivenessChip(r.effectiveness)}</TableCell>
                      <TableCell align="right">{r.delta_pct != null ? fmtPct(r.delta_pct) : '—'}</TableCell>
                      <TableCell>{significanceChip(r.significance)}</TableCell>
                      <TableCell><code>{r.primary_metric || '—'}</code></TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => { setDetailRow(r); setDetailOpen(true); }}>
                          Подробнее
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {actions.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>Нет действий за выбранные фильтры</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Action #{detailRow?.id} — {ACTION_TYPE_LABELS[detailRow?.action_type] || detailRow?.action_type}
        </DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">Связанная рекомендация</Typography>
                <Typography color="text.secondary">
                  {detailRow.recommendation_title || (detailRow.recommendation_id ? `#${detailRow.recommendation_id}` : '—')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Метрики ДО vs ПОСЛЕ</Typography>
                <MetricsCompare
                  before={detailRow.effect_snapshot_after?.metrics_before_recomputed
                    || detailRow.effect_snapshot_before?.metrics
                    || detailRow.effect_snapshot_before
                    || {}}
                  after={detailRow.effect_snapshot_after?.metrics || {}}
                  primary={detailRow.primary_metric}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Статистика</Typography>
                <pre style={{ background: '#f5f5f5', padding: 8, fontSize: 12, overflowX: 'auto' }}>
{JSON.stringify(detailRow.effect_snapshot_after?.statistics || {}, null, 2)}
                </pre>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Source / Notes</Typography>
                <Typography variant="body2"><b>Источник:</b> {detailRow.effect_snapshot_after?.source || '—'}</Typography>
                {Array.isArray(detailRow.effect_snapshot_after?.notes) && detailRow.effect_snapshot_after.notes.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {detailRow.effect_snapshot_after.notes.map((n, i) => <li key={i}><Typography variant="caption">{n}</Typography></li>)}
                  </ul>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function MetricsCompare({ before, after, primary }) {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  if (keys.length === 0) return <Typography color="text.secondary">Нет метрик</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Метрика</TableCell>
          <TableCell align="right">До</TableCell>
          <TableCell align="right">После</TableCell>
          <TableCell align="right">Δ</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {keys.map((k) => {
          const b = Number(before?.[k]);
          const a = Number(after?.[k]);
          const delta = Number.isFinite(b) && Number.isFinite(a) ? a - b : null;
          const isPrimary = k === primary;
          return (
            <TableRow key={k}>
              <TableCell sx={{ fontWeight: isPrimary ? 700 : 400 }}>
                <code>{k}</code>{isPrimary && <Chip size="small" sx={{ ml: 1 }} color="primary" label="primary" />}
              </TableCell>
              <TableCell align="right">{Number.isFinite(b) ? fmtNumber(b, 2) : '—'}</TableCell>
              <TableCell align="right">{Number.isFinite(a) ? fmtNumber(a, 2) : '—'}</TableCell>
              <TableCell align="right">
                {delta != null ? (
                  <Typography component="span" color={delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary'}>
                    {delta > 0 ? '+' : ''}{fmtNumber(delta, 2)}
                  </Typography>
                ) : '—'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
